import { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { X, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { errorMessage } from '../../lib/errors';

// Initialised at module scope so the Stripe.js load is shared rather than
// repeated on every mount.
//
// Deliberately NOT `|| ''`. `loadStripe('')` makes Stripe.js throw
// "Please call Stripe() with your publishable key. You used an empty string." —
// and because this happens at module scope in the main bundle, it is an uncaught
// exception on EVERY page load, before React mounts, showing the user a blank
// screen. The `|| ''` actively turned "key not configured" into "guaranteed
// crash"; without it the value is simply undefined and nothing is called.
//
// A missing key must degrade to a clear message inside this modal, which is what
// the null branch below does. Found by the post-deploy smoke test on 2026-08-17.
const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY?.trim();
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;

interface SubscriptionCheckoutModalProps {
    isOpen: boolean;
    onClose: () => void;
    priceId: string;
    orgId: string;
}

export function SubscriptionCheckoutModal({ isOpen, onClose, priceId, orgId }: SubscriptionCheckoutModalProps) {
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen) {
            // Reset state when closed
            setClientSecret(null);
            setError(null);
            return;
        }

        // Fail fast rather than creating a Checkout Session that could never be
        // rendered — the provider below needs a live `stripePromise`, so without
        // one the user would otherwise sit on the spinner for ever while a real
        // session was consumed on Stripe's side.
        if (!stripePromise) {
            setError(
                'Checkout is unavailable because the payment provider is not configured. ' +
                'This is a problem on our side, not with your card — please contact support.'
            );
            setLoading(false);
            return;
        }

        const createSession = async () => {
            setLoading(true);
            setError(null);
            try {
                // Call the edge function to create a Checkout Session
                const { data, error: fnError } = await supabase.functions.invoke('create-checkout-session', {
                    body: {
                        priceId,
                        metadata: {
                            org_id: orgId
                        }
                    }
                });

                if (fnError) throw fnError;

                if (data?.clientSecret) {
                    setClientSecret(data.clientSecret);
                } else {
                    throw new Error(data?.error || 'Failed to initialise checkout');
                }
            } catch (err) {
                console.error('Checkout Initialisation Error:', err);
                // create-checkout-session now returns a real status code on
                // failure (it used to return 200 with an error body, so a failed
                // checkout looked successful). supabase-js surfaces a non-2xx as
                // FunctionsHttpError and leaves the body unparsed, so read it
                // here — otherwise the user sees "non-2xx status code" instead of
                // the actionable message the function sent.
                let message = errorMessage(err, 'Something went wrong while starting the checkout process.');
                try {
                    // supabase-js attaches the raw Response as `context` on a
                    // FunctionsHttpError. Narrowed rather than asserted: `err` is
                    // `unknown`, and this shape is a supabase-js implementation
                    // detail that a version bump could remove — in which case the
                    // generic message above is still shown, rather than a crash
                    // inside the error handler.
                    const ctx = (err as { context?: { json?: () => Promise<unknown> } })?.context;
                    const body = await ctx?.json?.() as { error?: string } | undefined;
                    if (body?.error) message = body.error;
                } catch {
                    // Body already consumed or not JSON — keep the generic message.
                }
                setError(message);
            } finally {
                setLoading(false);
            }
        };

        createSession();
    }, [isOpen, priceId, orgId]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-scrim/80 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-panel border border-line rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="px-6 py-4 border-b border-line flex items-center justify-between sticky top-0 bg-panel z-10">
                    <h2 className="text-xl font-bold text-fg">Upgrade Subscription</h2>
                    <button
                        onClick={onClose}
                        className="p-2 text-fg-2 hover:text-fg hover:bg-raised rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto min-h-[400px]">
                    {loading && !clientSecret && !error && (
                        <div className="flex flex-col items-center justify-center h-full space-y-4 py-20">
                            <Loader2 className="w-10 h-10 animate-spin text-accent" />
                            <p className="text-fg-2">Preparing secure checkout...</p>
                        </div>
                    )}

                    {error && (
                        <div className="p-4 bg-danger/10 border border-danger/20 rounded-xl text-center">
                            <p className="text-danger font-medium mb-2">Checkout Error</p>
                            <p className="text-danger/80 text-sm">{error}</p>
                            <button
                                onClick={onClose}
                                className="mt-4 px-4 py-2 bg-danger/20 text-danger rounded-lg text-sm font-medium hover:bg-danger/30 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    )}

                    {clientSecret && stripePromise && !error && (
                        <EmbeddedCheckoutProvider key={clientSecret} stripe={stripePromise} options={{ clientSecret }}>
                            <EmbeddedCheckout />
                        </EmbeddedCheckoutProvider>
                    )}
                </div>
            </div>
        </div>
    );
}
